// src/utils/sessionExpiry.js
//
// One place that ends an expired session: clear the stored login, tell the UI
// (ToastContext shows "Your session expired"), then send the user to the right
// login page. Used by the axios interceptor in services/api.js AND by the
// window.fetch guard below — several screens (care plan, care team, chat,
// patient summary, …) call fetch() directly and never went through axios, so an
// expired token on those just rendered "Could not load…" instead of logging the
// user out.

import { dropPushSubscription } from './pushSubscription';

let sessionExpiredHandled = false;

// Guards against firing more than once: a burst of in-flight requests all
// 401/403 when the token expires, and each would otherwise stack its own toast
// and redirect timer. The redirect navigates away, so the flag is never reset.
export function handleSessionExpired() {
  if (sessionExpiredHandled) return;
  sessionExpiredHandled = true;

  const stored       = localStorage.getItem('previu_user');
  const isSubscriber = !!localStorage.getItem('previu_subscriber') && !stored;
  const isPatient    = !isSubscriber && !!localStorage.getItem('previu_patient') && !stored;

  // The token is already dead, so only the local half is possible; the server
  // prunes the row on its next push (410).
  dropPushSubscription({ notifyServer: false });

  localStorage.removeItem('previu_token');
  localStorage.removeItem('previu_user');
  localStorage.removeItem('previu_patient');
  localStorage.removeItem('previu_subscriber');

  window.dispatchEvent(new CustomEvent('previu:session-expired'));

  setTimeout(() => {
    window.location.href = isSubscriber ? '/subscriber-login' : isPatient ? '/login' : '/nav-login';
  }, 1200);
}

// Wraps window.fetch so a 401/403 from the Previu API ends the session, the
// same as the axios interceptor does. Scoped so it can't misfire:
//   - only requests to the API base (third-party calls are left alone),
//   - only while a token is stored (a wrong-password 401 on login has no
//     session to expire), and never for /auth/ endpoints.
// The original response is returned untouched, so callers' own error handling
// still runs.
//
// NEVER_ENDS_SESSION lists API paths whose 401/403 must not log anyone out:
// Ask Previu's translation (services/chatTranslateAPI.js) falls back to English
// on any failure, and a session that has really expired is still caught by
// the chat request that runs alongside it. The preferred-language setting
// (services/languageAPI.js) hides itself or shows an error instead, and every
// other request on the page still catches a real expiry.
const NEVER_ENDS_SESSION = ['/chat/translate', '/me/language'];

export function installFetchSessionGuard(apiBase) {
  if (typeof window === 'undefined' || window.__previuFetchGuard) return;
  window.__previuFetchGuard = true;

  const nativeFetch = window.fetch.bind(window);
  window.fetch = async (input, init) => {
    const res = await nativeFetch(input, init);
    if (res.status === 401 || res.status === 403) {
      const url = typeof input === 'string' ? input : input?.url || '';
      if (url.startsWith(apiBase) && !url.startsWith(`${apiBase}/auth/`)
          && !NEVER_ENDS_SESSION.some(p => url.split('?')[0] === `${apiBase}${p}`)
          && localStorage.getItem('previu_token')) {
        handleSessionExpired();
      }
    }
    return res;
  };
}
